import React from "react";
import { CardLayout } from "components/CardLayout";
import { Button, Icon, DateInput } from "@octano/global-ui";
import DownloadLeavesSummaryButton from "../../components/pdf/leaves-summary/DownloadLeavesSummaryButton";
import LeavesHistoricTable from "views/Leaves/parts/LeavesHistoricTable";
import LeavesRequestsTable from "views/Leaves/parts/LeavesRequestTable";
import { LeavesSummary } from "views/Leaves/parts/LeavesSummary";
import { LeavesReviewResponse } from "./types";

export interface LeavesReviewProps {
  selectedUser: { id: number; full_name: string; rut?: string } | null;
  selectedDate: Date | null;
  setSelectedDate: (d: Date | null) => void;
  triggerDateSearch: () => void;
  leavesData?: LeavesReviewResponse & { refresh: () => Promise<void> };
}

const LeavesReview: React.FC<LeavesReviewProps> = ({
  selectedUser,
  selectedDate,
  setSelectedDate,
  triggerDateSearch,
  leavesData,
}) => {
  return (
    <section id="leaves-review" className="pt-3">
      <CardLayout>
        {!selectedUser && (
          <div
            className="d-flex flex-column align-items-center justify-content-center w-100"
            style={{ minHeight: 380 }}
          >
            <Icon name="information" color="info" size={56} />
            <h3 className="mt-4 fs-22 fw-500 text-dark text-center">
              Para continuar, realiza una búsqueda.
            </h3>
            <p
              className="mt-2 fs-16 text-light text-center"
              style={{ maxWidth: 520 }}
            >
              Para consultar la información, realiza una búsqueda por nombre o
              RUT.
            </p>
          </div>
        )}
        {selectedUser && (
          <div
            className="d-flex justify-content-between align-items-start"
            style={{ gap: 24, width: "100%" }}
          >
            <div className="flex-grow-1" style={{ minWidth: 320 }}>
              <p
                className="text-light fw-400 fs-16 mb-4"
                style={{ maxWidth: 620 }}
              >
                A continuación, se muestran los días de permisos y vacaciones
                disponibles del funcionario hasta la fecha actual. Si deseas
                consultar otra fecha, utiliza el filtro por fecha.
              </p>
              <div
                className="mb-3 d-flex align-items-end"
                style={{ gap: 20, flexWrap: "nowrap" }}
              >
                <div style={{ width: 300, height: 55 }}>
                  <DateInput
                    name="availableUntil"
                    label="DÍAS DISPONIBLES HASTA"
                    value={selectedDate}
                    disabled={!selectedUser}
                    onChange={(val) => {
                      if (!val) {
                        setSelectedDate(null);
                      } else if (val instanceof Date) {
                        setSelectedDate(val);
                      } else {
                        setSelectedDate(new Date(val));
                      }
                    }}
                  />
                </div>
                <Button
                  size="md"
                  text="BUSCAR"
                  disabled={!selectedUser}
                  onClick={triggerDateSearch}
                />
              </div>
            </div>
            {leavesData && (
              <div
                className="d-flex flex-column"
                style={{ gap: 16, flexShrink: 0 }}
              >
                <div
                  className="d-flex align-items-stretch flex-wrap"
                  style={{ gap: 16 }}
                >
                  <LeavesSummary
                    description="Días de vacaciones periodo actual"
                    tooltipTitle="Resumen de vacaciones disponibles"
                    boxId="tooltip-vacaciones"
                    holidays={{
                      previousPeriodAccumulatedDays:
                        leavesData.holidaysStatus.p_acumulados,
                      currentPeriodAccumulatedDays:
                        leavesData.holidaysStatus.p_periodo,
                      zoneDays: leavesData.holidaysStatus.p_zona,
                      progressiveDays: leavesData.holidaysStatus.p_progresivo,
                      interruptedDays:
                        leavesData.holidaysStatus.p_interrumpidos,
                      currentPeriodUsedDays:
                        leavesData.holidaysStatus.p_tomados_periodo,
                      transferredDays: leavesData.holidaysStatus.p_transferidos,
                      takenDays: leavesData.holidaysStatus.p_adelantados,
                      totalAvailableDays:
                        leavesData.holidaysStatus.p_disponibles,
                      error: leavesData.holidaysStatus.p_cod_error,
                    }}
                    total={leavesData.holidaysStatus.p_disponibles}
                    code_error={leavesData.holidaysStatus.p_cod_error}
                  />
                  <LeavesSummary
                    description="Días administrativos periodo actual"
                    tooltipTitle="Resumen de días administrativos disponibles"
                    boxId="tooltip-administrativos"
                    holidays={null}
                    total={leavesData.permissionsStatus.p_disponibles}
                    code_error={leavesData.permissionsStatus.p_cod_error}
                  />
                </div>
                <div
                  className="d-flex align-items-start justify-content-end w-100"
                  style={{ paddingTop: 15 }}
                >
                  <DownloadLeavesSummaryButton
                    data={leavesData}
                    selectedDate={selectedDate}
                    userFullName={selectedUser?.full_name}
                    userRut={selectedUser?.rut}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {selectedUser && leavesData && (
          <>
            <div className="mt-5">
              <h3 className="text-light fw-400 fs-20 mb-3">
                Solicitudes de Permisos
              </h3>
              <LeavesRequestsTable
                data={leavesData.requests}
                refresh={leavesData.refresh}
                hideDelete
              />
            </div>
            <div className="mt-4">
              <h3 className="text-light fw-400 fs-20 mb-3">
                Histórico de Permisos
              </h3>
              <LeavesHistoricTable data={leavesData.registereds} />
            </div>
          </>
        )}
      </CardLayout>
    </section>
  );
};

export default LeavesReview;
